import { DOMContainer, Rectangle } from 'pixi.js';

const BORDER_WIDTH = 5;
const BORDER_RADIUS = 17;
const FONT_SIZE = 38;
const LETTER_SPACING = 0;
const PADDING_LEFT = 20;
const PADDING_RIGHT = 80;

// DOMPipe places the element from worldTransform, which skips the offset @pixi/layout
// writes into the local transform. Measure with toGlobal() against the canvas, same as ScaledInput.
export default class DomInput extends DOMContainer {
    constructor({ canvas, width, height, placeholder = '' }) {
        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'dom-input';
        input.placeholder = placeholder;
        input.setAttribute('aria-label', placeholder || 'Search collection');
        input.autocomplete = 'off';
        input.spellcheck = false;

        super({ element: input });
        // Keep DOMPipe from rewriting the fixed position every frame.
        this.renderable = false;
        // A 0-size DOM element makes layout divide by zero and write NaN into the transform.
        this.boundsArea = new Rectangle(0, 0, width, height);

        this.canvas = canvas;
        this.inputWidth = width;
        this.inputHeight = height;
        this.onInputBinding = this.onInput.bind(this);
        this.onTouchStartBinding = (event) => event.stopPropagation();
        this.onTouchMoveBinding = (event) => event.preventDefault();
        input.addEventListener('input', this.onInputBinding);
        input.addEventListener('touchstart', this.onTouchStartBinding);
        input.addEventListener('touchmove', this.onTouchMoveBinding, { passive: false });

        this.layout = {
            width,
            height,
            objectFit: 'none',
            objectPosition: 'left top',
        };

        this.positionInputField = this.positionInputField.bind(this);
        this.once('layout', this.positionInputField);
        window.addEventListener('resize', this.positionInputField);
    }

    onInput() {
        this.onTextChange?.(this.element.value);
    }

    setShown(shown) {
        const input = this.element;
        if (!input) return;
        input.classList.toggle('dom-input--hidden', !shown);
        if (!shown) input.blur();
    }

    positionInputField() {
        const input = this.element;
        if (!input || !this.canvas || !this.parent) return;

        if (input.parentNode !== document.body) {
            document.body.appendChild(input);
        }

        const rect = this.canvas.getBoundingClientRect();
        const canvasWidth = this.canvas.width;
        const canvasHeight = this.canvas.height;
        if (!canvasWidth || !canvasHeight || !rect.width || !rect.height) return;

        const scaleX = rect.width / canvasWidth;
        const scaleY = rect.height / canvasHeight;
        // toGlobal() is NaN here: layout bakes a bad transform from the DOM element's 0 size.
        // Walk the same offsets layout applies on top of each node's x/y.
        const topLeft = { x: 0, y: 0 };
        for (let node = this; node; node = node.parent) {
            topLeft.x += node.x + (node.layout?.computedPixiLayout.x ?? 0);
            topLeft.y += node.y + (node.layout?.computedPixiLayout.y ?? 0);
        }
        const width = this.inputWidth * scaleX;
        const height = this.inputHeight * scaleY;
        const borderWidth = BORDER_WIDTH * scaleY;

        Object.assign(input.style, {
            left: `${rect.left + topLeft.x * scaleX}px`,
            top: `${rect.top + topLeft.y * scaleY}px`,
            width: `${width}px`,
            height: `${height}px`,
            padding: `0 ${PADDING_RIGHT * scaleX}px 0 ${PADDING_LEFT * scaleX}px`,
            borderWidth: `${borderWidth}px`,
            borderRadius: `${BORDER_RADIUS * scaleX}px`,
            fontSize: `${FONT_SIZE * scaleY}px`,
            letterSpacing: `${LETTER_SPACING * scaleX}px`,
            lineHeight: `${height - borderWidth * 2}px`,
        });
    }

    destroy(options) {
        this.off('layout', this.positionInputField);
        window.removeEventListener('resize', this.positionInputField);
        const input = this.element;
        input?.removeEventListener('input', this.onInputBinding);
        input?.removeEventListener('touchstart', this.onTouchStartBinding);
        input?.removeEventListener('touchmove', this.onTouchMoveBinding);
        super.destroy(options);
    }
}
